import { ReactElement, useEffect, useState } from 'react';
import styles from './results.module.scss';
import Select, { SingleValue, StylesConfig } from 'react-select';
import { useRouter } from 'next/router';
import ResultsFilterView from '@utmb/types/view/ResultsFilterView';

interface ResultsRaceSelectorProps {
    results: ResultsFilterView[];
    year: string | string[] | undefined;
}

export const ResultsRaceSelector = (props: ResultsRaceSelectorProps): ReactElement => {
    const { results, year } = props;
    const { pathname, push, query } = useRouter();

    const filteredRaces = results
        .filter((result) => result.year.toString() === year)
        .map((result) => result.races)
        .reduce((acc, val) => acc.concat(val), []);

    const options = filteredRaces.filter((race) => race.hasResults).map((race) => ({ label: race.raceName, value: race.raceUri }));
    const [selectedRaceId, setSelectedRaceId] = useState(options.find((option) => option.value === query.raceUri)?.value?.split('.')[0]);

    const selectedOption = options.find((option) => option.value === query.raceUri);

    const handleChange = (raceOption: SingleValue<{ value: string; label: string }>) => {
        const selected = filteredRaces.find((race) => raceOption?.value === race.raceUri);
        if (selected?.hasResults) {
            const raceUri = selected.raceUri;
            delete query.page;
            setSelectedRaceId(raceUri.split('.')[0]);
            push({ pathname, query: { ...query, year, raceUri } }, undefined, { shallow: true }).then();
        }
    };

    useEffect(() => {
        const selected = options.find((opt) => opt.value?.split('.')[0] === selectedRaceId);
        if (selected) handleChange(selected);
    }, [year]);

    const customStyles: StylesConfig = {
        menu: (provided) => ({
            ...provided,
            margin: 0,
        }),
        dropdownIndicator: () => ({}),
        indicatorSeparator: (provided) => ({
            ...provided,
        }),
        control: (base) => ({
            ...base,
            height: 60,
            padding: '0 10px',
            borderRadius: 0,
            boxShadow: '3px 3px 11px rgba(0, 0, 0, 0.1)',
        }),
        option: (base, state) => ({
            ...base,
            cursor: 'pointer',
            height: 60,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: state.isSelected ? 'var(--utmb-color-primary)' : 'var(--utmb-color-whhite)',
        }),
        menuList: (provided) => ({
            ...provided,
            paddingTop: 0,
        }),
    };

    return (
        <div className={styles.selectorRaceContainer}>
            <Select
                instanceId="race-selector"
                isSearchable={false}
                menuShouldScrollIntoView={true}
                value={selectedOption || null}
                onChange={handleChange}
                styles={customStyles as never}
                options={options}
            />
        </div>
    );
};
